import axios, { AxiosHeaders } from 'axios'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { i18n } from '@/i18n'

export const http = axios.create({
  baseURL: '/api',
  withCredentials: true,
})

http.interceptors.request.use((config) => {
  const headers = AxiosHeaders.from(config.headers)

  headers.set('Accept-Language', i18n.global.locale.value)
  config.headers = headers

  return config
})

export function createResource(client: AxiosInstance) {
  return {
    index: () => client.get<string>('/').then((response) => response.data),
    auth: {
      login: (data: SignInRequest, config?: AxiosRequestConfig) =>
        client.post<User>('/auth/login', data, config).then((response) => response.data),
      logout: (config?: AxiosRequestConfig) =>
        client
          .post<{ ok: true }>('/auth/logout', undefined, config)
          .then((response) => response.data),
    },
    user: {
      current: (config?: AxiosRequestConfig) =>
        client.get<User>('/user/current', config).then((response) => response.data),
      byId: (id: number, config?: AxiosRequestConfig) =>
        client.get<User>(`/user/${id}`, config).then((response) => response.data),
      list: (config?: AxiosRequestConfig) =>
        client.get<User[]>('/user', config).then((response) => response.data),
      create: (data: CreateUserRequest, config?: AxiosRequestConfig) =>
        client.post<CreatedUser>('/user', data, config).then((response) => response.data),
      changePassword: (password: string, currentPassword?: string, config?: AxiosRequestConfig) =>
        client
          .patch<{
            ok: true
          }>(
            '/user/current/password',
            { password, ...(currentPassword && { currentPassword }) },
            config,
          )
          .then((response) => response.data),
      updateProfile: (data: UpdateProfileRequest, config?: AxiosRequestConfig) =>
        client.patch<{ ok: true }>('/user/current', data, config).then((response) => response.data),
      delete: (id: number, config?: AxiosRequestConfig) =>
        client.delete<void>(`/user/${id}`, config).then((response) => response.data),
      promote: (id: number, config?: AxiosRequestConfig) =>
        client
          .patch<{ ok: true }>(`/user/${id}/admin`, undefined, config)
          .then((response) => response.data),
      update: (id: number, data: UpdateUserRequest, config?: AxiosRequestConfig) =>
        client.patch<{ ok: true }>(`/user/${id}`, data, config).then((response) => response.data),
      resetPassword: (id: number, config?: AxiosRequestConfig) =>
        client
          .post<{ password: string }>(`/user/${id}/password-reset`, undefined, config)
          .then((response) => response.data),
    },
    image: {
      upload: (image: File, config?: AxiosRequestConfig) => {
        const data = new FormData()
        data.append('image', image)

        return client.post<Image>('/image', data, config).then((response) => response.data)
      },
    },
    aiRequestAudit: {
      list: (params?: PaginationQuery, config?: AxiosRequestConfig) =>
        client
          .get<Paginated<AIRequestAudit>>('/ai-request-audit', { ...config, params })
          .then((response) => response.data),
      byId: (id: number, config?: AxiosRequestConfig) =>
        client
          .get<AIRequestAudit>(`/ai-request-audit/${id}`, config)
          .then((response) => response.data),
    },
    systemPrompt: {
      current: (documentType: DocumentType, config?: AxiosRequestConfig) =>
        client
          .get<{ prompt: string }>(`/system-prompt/${documentType}/current`, config)
          .then((response) => response.data),
      list: (documentType: DocumentType, config?: AxiosRequestConfig) =>
        client
          .get<SystemPrompt[]>(`/system-prompt/${documentType}`, config)
          .then((response) => response.data),
      save: (documentType: DocumentType, prompt: string, config?: AxiosRequestConfig) =>
        client
          .post<SystemPrompt>(`/system-prompt/${documentType}`, { prompt }, config)
          .then((response) => response.data),
      reset: (documentType: DocumentType, config?: AxiosRequestConfig) =>
        client
          .post<SystemPrompt>(`/system-prompt/${documentType}/reset`, undefined, config)
          .then((response) => response.data),
      rollback: (id: number, config?: AxiosRequestConfig) =>
        client
          .post<SystemPrompt>(`/system-prompt/rollback/${id}`, undefined, config)
          .then((response) => response.data),
      delete: (id: number, config?: AxiosRequestConfig) =>
        client
          .delete<void>(`/system-prompt/history/${id}`, config)
          .then((response) => response.data),
    },
    department: {
      list: (config?: AxiosRequestConfig) =>
        client.get<Department[]>('/department', config).then((response) => response.data),
      save: (data: CreateDepartmentRequest, config?: AxiosRequestConfig) =>
        client.post<Department>('/department', data, config).then((response) => response.data),
      delete: (id: number, config?: AxiosRequestConfig) =>
        client.delete<void>(`/department/${id}`, config).then((response) => response.data),
    },
    document: {
      summary: (config?: AxiosRequestConfig) =>
        client
          .get<DocumentDashboardSummary>('/document/stats/summary', config)
          .then((response) => response.data),
      list: (params?: DocumentListQuery, config?: AxiosRequestConfig) =>
        client
          .get<Paginated<Document>>('/document', { ...config, params })
          .then((response) => response.data),
      byId: (id: number, config?: AxiosRequestConfig) =>
        client.get<Document>(`/document/${id}`, config).then((response) => response.data),
      create: (data: CreateDocumentRequest, config?: AxiosRequestConfig) =>
        client.post<Document>('/document', data, config).then((response) => response.data),
      update: (id: number, data: UpdateDocumentRequest, config?: AxiosRequestConfig) =>
        client.patch<Document>(`/document/${id}`, data, config).then((response) => response.data),
      duplicate: (id: number, config?: AxiosRequestConfig) =>
        client
          .post<Document>(`/document/${id}/duplicate`, undefined, config)
          .then((response) => response.data),
      replaceMetadata: (id: number, data: DocumentMeta, config?: AxiosRequestConfig) =>
        client
          .put<Document>(`/document/${id}/meta/instructions`, data, config)
          .then((response) => response.data),
      replaceTools: (id: number, data: DocumentToolRequest[], config?: AxiosRequestConfig) =>
        client
          .put<Document>(`/document/${id}/tools`, data, config)
          .then((response) => response.data),
      replaceResources: (
        id: number,
        data: DocumentResourceRequest[],
        config?: AxiosRequestConfig,
      ) =>
        client
          .put<Document>(`/document/${id}/resources`, data, config)
          .then((response) => response.data),
      replaceInstructions: (
        id: number,
        data: DocumentInstructionRequest[],
        config?: AxiosRequestConfig,
      ) =>
        client
          .put<Document>(`/document/${id}/instructions`, data, config)
          .then((response) => response.data),
      delete: (id: number, config?: AxiosRequestConfig) =>
        client.delete<boolean>(`/document/${id}`, config).then((response) => response.data),
      suggestionStatus: (id: number, config?: AxiosRequestConfig) =>
        client
          .get<DocumentSuggestionStatus>(`/document/${id}/suggestions/status`, config)
          .then((response) => response.data),
      requestReview: (id: number, config?: AxiosRequestConfig) =>
        client
          .post<Document>(`/document/${id}/review`, undefined, config)
          .then((response) => response.data),
      convert: (id: number, targetType: DocumentType, config?: AxiosRequestConfig) =>
        client
          .post<Document>(`/document/${id}/convert`, { targetType }, config)
          .then((response) => response.data),
      applySuggestion: (id: number, suggestionId: number, config?: AxiosRequestConfig) =>
        client
          .post<Document>(`/document/${id}/suggestions/${suggestionId}/apply`, undefined, config)
          .then((response) => response.data),
      denySuggestion: (id: number, suggestionId: number, config?: AxiosRequestConfig) =>
        client
          .post<Document>(`/document/${id}/suggestions/${suggestionId}/deny`, undefined, config)
          .then((response) => response.data),
    },
  }
}

export const api = createResource(http)

export interface SignInRequest {
  username: string
  password: string
}

export interface User {
  id: number
  email: string
  username: string
  firstName: string | null
  lastName: string | null
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  isAdmin: boolean
  mustChangePassword: boolean
}

export interface CreateUserRequest {
  email: string
  username: string
  firstName: string
  lastName: string
}

export interface CreatedUser {
  user: User
  password: string
}

export interface UpdateUserRequest {
  email: string
  firstName: string
  lastName: string
}

export interface UpdateProfileRequest {
  firstName: string
  lastName: string
}

export type DocumentType = 'instructions' | 'free_input'

export interface SystemPrompt {
  id: number
  documentType: DocumentType
  prompt: string
  createdAt: string
}

export interface Department {
  id: number
  name: string
  sourceText: string | null
  targetType: DocumentType | null
}

export interface CreateDepartmentRequest {
  name: string
}

export interface DocumentMeta {
  purpose: string
  scope: string
  department: string
}

export interface DocumentTool {
  id?: number
  index: number
  name: string
  images: Image[]
}

export interface DocumentToolRequest extends Omit<DocumentTool, 'images'> {
  images?: Array<Pick<Image, 'id'>>
}

export interface DocumentResource {
  id?: number
  index: number
  name: string
  type: string
  articleNumber: string
  images: Image[]
}

export interface DocumentResourceRequest extends Omit<DocumentResource, 'images'> {
  images?: Array<Pick<Image, 'id'>>
}

export interface Image {
  id: number
  originalName: string
  mimeType: string
  storageKey: string
  url: string
}

export interface DocumentInstruction {
  id?: number
  index: number
  title: string
  content: string
  images: Image[]
}

export interface DocumentInstructionRequest {
  id?: number
  index: number
  title: string
  content: string
  images?: Array<Pick<Image, 'id'>>
}

export interface Document {
  id: number
  type: DocumentType
  name: string
  suggestionsReady: boolean
  summary: string | null
  suggestionSummary: string | null
  documentRating: number | null
  documentRatingExplanation: string | null
  meta: DocumentMeta | null
  instructionsMeta?: DocumentMeta | null
  tools: DocumentTool[]
  resources: DocumentResource[]
  instructions: DocumentInstruction[]
  suggestions: DocumentSuggestion[]
  sourceText: string | null
  targetType: DocumentType | null
}

export interface DocumentListQuery {
  page?: number
  limit?: number
}

export interface DocumentDashboardSummary {
  total: number
  ready: number
  processing: number
}

export type PaginationQuery = DocumentListQuery

export interface Paginated<T> {
  data: T[]
  meta: {
    itemsPerPage: number
    totalItems: number
    currentPage: number
    totalPages: number
  }
}

export interface AIRequestAudit {
  id: number
  userId: number
  userName: string
  documentId: number
  documentName: string
  request?: Record<string, unknown> | null
  response?: unknown
  error: string | null
  requestedAt: string
  completedAt: string | null
}

export interface DocumentSuggestion {
  id: number
  targetType: 'document' | 'instruction' | 'resource' | 'tool' | 'meta'
  action: 'insert_item' | 'remove_item' | 'update_field'
  targetId: number | null
  targetField: string | null
  summary: Record<string, string> | string
  suggestedValue: unknown
  applied: boolean
}

export interface DocumentSuggestionStatus {
  suggestionsReady: boolean
}

export interface CreateDocumentRequest {
  type?: DocumentType
  name: string
  sourceText?: string
  meta?: DocumentMeta
  tools?: DocumentToolRequest[]
  resources?: DocumentResourceRequest[]
  instructions?: DocumentInstructionRequest[]
}

export interface UpdateDocumentRequest {
  type?: DocumentType
  name?: string
  sourceText?: string
}
